/**
 * CAMERADEBUG.JSX - Contrôles de Debug pour la Caméra
 * 
 * Ce composant ajoute des contrôles Leva pour débugger et ajuster
 * les paramètres de la caméra en temps réel.
 * 
 * FONCTIONNALITÉS :
 * - Ajustement de la position de la caméra (X, Y, Z)
 * - Ajustement du target OrbitControls (X, Y, Z)
 * - Modification du FOV
 * - Ajustement des plans near/far
 * - Bouton Reset pour revenir aux valeurs par défaut
 * - Bouton Log pour afficher les valeurs dans la console
 * 
 * NOTE : Les paramètres OrbitControls (limites, vitesses) sont dans Scene.jsx
 * 
 * UTILISATION :
 * Ce composant doit être placé dans la scène R3F pour avoir accès
 * à la caméra et aux contrôles via useThree.
 */

import { useEffect, useCallback } from 'react'
import { useControls, button } from 'leva'
import { useThree } from '@react-three/fiber'

function CameraDebug() {
  const { camera, controls } = useThree()

  // Valeurs par défaut (identiques au code vanilla)
  const defaults = {
    // Position caméra
    // cameraPosX: 10.8,
    // cameraPosY: -13.8,
    // cameraPosZ: 11.0,
    cameraPosX: 8.3,
    cameraPosY: -8.8,
    cameraPosZ: 16.8,
    // Target OrbitControls
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    // Paramètres caméra
    fov: 54,
    near: 1,
    far: 80
  }

  // Fonction pour logger les valeurs actuelles
  const logCurrentValues = useCallback(() => {
    console.log('📷 Camera Debug - Valeurs actuelles :')
    console.log('\n📍 Position Caméra :')
    console.log(`  x: ${camera.position.x.toFixed(2)}`)
    console.log(`  y: ${camera.position.y.toFixed(2)}`)
    console.log(`  z: ${camera.position.z.toFixed(2)}`)
    console.log('\n🎯 Target OrbitControls :')
    console.log(`  x: ${controls.target.x.toFixed(2)}`)
    console.log(`  y: ${controls.target.y.toFixed(2)}`)
    console.log(`  z: ${controls.target.z.toFixed(2)}`)
    console.log('\n⚙️ Paramètres Caméra :')
    console.log(`  fov: ${camera.fov}°`)
    console.log(`  near: ${camera.near}`)
    console.log(`  far: ${camera.far}`)
    console.log('\n📋 Code à copier dans defaults :')
    console.log(`cameraPosX: ${camera.position.x.toFixed(1)},`)
    console.log(`cameraPosY: ${camera.position.y.toFixed(1)},`)
    console.log(`cameraPosZ: ${camera.position.z.toFixed(1)},`)
    console.log(`targetX: ${controls.target.x.toFixed(1)},`)
    console.log(`targetY: ${controls.target.y.toFixed(1)},`)
    console.log(`targetZ: ${controls.target.z.toFixed(1)},`)
    console.log(`fov: ${camera.fov},`)
    console.log(`near: ${camera.near},`)
    console.log(`far: ${camera.far}`)
  }, [camera, controls])

  // Fonction reset
  const resetToDefaults = useCallback(() => {
    set({
      cameraPosX: defaults.cameraPosX,
      cameraPosY: defaults.cameraPosY,
      cameraPosZ: defaults.cameraPosZ,
      targetX: defaults.targetX,
      targetY: defaults.targetY,
      targetZ: defaults.targetZ,
      fov: defaults.fov,
      near: defaults.near,
      far: defaults.far
    })
  }, [])

  // Contrôles Leva
  const [{
    cameraPosX, cameraPosY, cameraPosZ,
    targetX, targetY, targetZ,
    fov, near, far
  }, set] = useControls('Camera Debug', () => ({
    // Position de la caméra
    cameraPosX: {
      value: defaults.cameraPosX,
      min: -50,
      max: 50,
      step: 0.1,
      label: 'Camera X'
    },
    cameraPosY: {
      value: defaults.cameraPosY,
      min: -50,
      max: 50,
      step: 0.1,
      label: 'Camera Y'
    },
    cameraPosZ: {
      value: defaults.cameraPosZ,
      min: -50,
      max: 50,
      step: 0.1,
      label: 'Camera Z'
    },
    '---1': { value: '--- Target ---', editable: false },
    // Target OrbitControls
    targetX: {
      value: defaults.targetX,
      min: -20,
      max: 20,
      step: 0.1,
      label: 'Target X'
    },
    targetY: {
      value: defaults.targetY,
      min: -20,
      max: 20,
      step: 0.1,
      label: 'Target Y'
    },
    targetZ: {
      value: defaults.targetZ,
      min: -20,
      max: 20,
      step: 0.1,
      label: 'Target Z'
    },
    '---2': { value: '--- Paramètres ---', editable: false },
    // Paramètres caméra
    fov: {
      value: defaults.fov,
      min: 10,
      max: 120,
      step: 1,
      label: 'FOV (°)'
    },
    near: {
      value: defaults.near,
      min: 0.1,
      max: 10,
      step: 0.1,
      label: 'Near Plane'
    },
    far: {
      value: defaults.far,
      min: 10,
      max: 500,
      step: 10,
      label: 'Far Plane'
    },
    // Boutons d'action
    '---3': { value: '--- Actions ---', editable: false },
    logValues: button(() => logCurrentValues(), {
      label: '📋 Log Values'
    }),
    reset: button(() => resetToDefaults(), {
      label: '🔄 Reset to Default'
    })
  }),
  { collapsed: true }
  )

  // Mise à jour de la position de la caméra
  useEffect(() => {
    camera.position.set(cameraPosX, cameraPosY, cameraPosZ)
  }, [camera, cameraPosX, cameraPosY, cameraPosZ])

  // Mise à jour du FOV et des plans near/far
  useEffect(() => {
    camera.fov = fov
    camera.near = near
    camera.far = far
    camera.updateProjectionMatrix()
  }, [camera, fov, near, far])

  // Mise à jour du target OrbitControls
  useEffect(() => {
    if (controls) {
      controls.target.set(targetX, targetY, targetZ)
      controls.update()
    }
  }, [controls, targetX, targetY, targetZ])

  // Ce composant ne rend rien visuellement
  return null
}

export default CameraDebug
